import React, {useMemo, useState} from 'react';
import {Checkbox, FormControlLabel, FormGroup, IconButton, Tooltip, Typography, withStyles} from '@material-ui/core';
import {Delete, Refresh} from '@material-ui/icons';
import * as action from '../../../sc-core/Auth/store/authActions';
import {BackButton, hasAtLeastOnePermission, hasPermission, localization} from '../../../sc-utilities';
import {connect, useDispatch} from 'react-redux';
import {refreshList} from '../store/raspberryEraseStationSlice'
import {MuiTableRows} from '../../../../themes';
import RaspberryEraseStationListRow from "./RaspberryEraseStationListRow";
import * as raspberryEraseStationActions from "../store/raspberryEraseStationActions";
import MaterialTable from "material-table";
import Toolbar from "../../../sc-utilities/Toolbar";
import DeleteDialog from "../../../sc-customer/Customer/DeleteDialog";
import RaspberryEraseStationListEditRow from "./RaspberryEraseStationListEditRow";

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    typography: {
        marginTop: '8%'
    },
    title: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
        marginTop: '7px',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const getDataForStore = (data) => {
    return data.reduce((acc, obj) => {
        let Details = null;
        try {
            Details = JSON.parse(obj.Details);
        } catch (e) {
            obj.Details = "{}";
        }
        obj["sessionStatus"] = Details && Details.Status? Details.Status : "ready";
        acc[obj.EraseStationID] = obj;
        return acc;
    }, {})
}

const RaspberryEraseStationList = props => {
    const { classes, translations, permissions, activeCustomerID, saveColumnsFilter, tableColumns, tableRef, fetchCount, fetchList, bulkDelete, updateRaspberryEraseStation, tableActions } = props;
    const [deleteModal, setDeleteModal] = useState({ open: false, ids: [] });
    const [checkboxState, setCheckboxState] = useState(false);

    const columnObject = useMemo(() => tableColumns, [tableColumns]);
    const selectionPermissions = hasAtLeastOnePermission(permissions, ['raspberry_erase_station_delete']);

    // store
    const dispatch = useDispatch();

    const tableHeight = hasPermission(permissions, 'access_all_raspberry_erase_stations')? 325 : 295;

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        search: false,
        minBodyHeight: window.innerHeight - tableHeight < 300 ? 300 : window.innerHeight - tableHeight,
        maxBodyHeight: window.innerHeight - tableHeight,
        pageSizeOptions: rowsPageSizeInit,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false,
    });

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`RaspberryEraseStation`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    };

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    const fetchTableData = async (query) => {
        const q = filterQuery(query.filters);
        q['NotAssignedOnly'] = checkboxState;
        query['q'] = q
        const count = await fetchCount({...query, ActiveCustomerID: activeCustomerID});
        let result = [];
        if (count > 0) {
            result = count ? await fetchList({...query, ActiveCustomerID: activeCustomerID}) : [];
            if (result) {
                const dataForStore = getDataForStore(JSON.parse(JSON.stringify(result)));
                dispatch(refreshList(dataForStore));
            }
        }

        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result?? [], page: query.page, pageSize: pageSize, totalCount: result?.length };
    }

    const handleCheckboxChange = () => {
        setCheckboxState(prevState => !prevState);
        tableRef.current && tableRef.current.onQueryChange();
    }

    const tableTitle = () => {
        return <div className={classes.title}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography> {translations["raspberry_erase_stations"] || 'Work Dongles'}</Typography>
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
            {hasPermission(permissions, 'access_all_raspberry_erase_stations') &&
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Checkbox
                                id="checkbox_locked"
                                name={"show_only_not_assigned"}
                                checked={checkboxState}
                                onChange={handleCheckboxChange}
                                color={'primary'}
                            />
                        }
                        label={translations["show_only_not_assigned"] || "Show only not assigned"}
                    />
                </FormGroup>}
        </div>
    };

    const handleCloseSafe = () => {
        setDeleteModal({ open: false, ids: [] });
    };

    const handleCloseDanger = async () => {
        await bulkDelete({ RaspberryEraseStationIDs: deleteModal.ids, ActiveCustomerID: activeCustomerID });
        setDeleteModal({ open: false, ids: [] });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = ids => {
        setDeleteModal({ open: true, ids });
    };

    const toolbarActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["raspberry_erase_stations"] || "Work Dongles"}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(raspberry => raspberry.EraseStationID))
        }
    ];

    return (
        <>
            <MaterialTable
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                components={{
                    Row: props => <RaspberryEraseStationListRow {...props} id={props.data.EraseStationID} translations={translations} activeCustomerID={activeCustomerID} />,
                    Toolbar: props => <Toolbar data={props} actions={toolbarActions} selectedRows={props.selectedRows} translations={translations} />,
                    EditRow: props => <RaspberryEraseStationListEditRow {...props} id={props.data.EraseStationID} updateRaspberryEraseStation={updateRaspberryEraseStation} activeCustomerID={activeCustomerID}/>
                }}
                localization={localization(translations)}
                tableRef={tableRef}
                options={tableOptions}
                onChangeRowsPerPage={(size) => pageSize = size}
                onChangeColumnHidden={columnHidden}
                editable={{
                    onRowUpdate: () => {},
                }}
                actions={tableActions}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />

            {deleteModal.open &&
            <DeleteDialog
                open={deleteModal.open}
                entityName={translations["raspberry_erase_stations"] || "Work Dongles"}
                handleCloseSafe={handleCloseSafe}
                handleCloseDanger={handleCloseDanger}
                translations={translations} />}
        </>
    )
}

const mapDispatchToProps = dispatch => {
    return {
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        fetchCount: data => dispatch(raspberryEraseStationActions.fetchRaspberryEraseStationsCount(data)),
        fetchList: data => dispatch(raspberryEraseStationActions.fetchRaspberryEraseStationsList(data)),
        bulkDelete: data => dispatch(raspberryEraseStationActions.bulkDeleteRaspberryEraseStations(data)),
        updateRaspberryEraseStation: data => dispatch(raspberryEraseStationActions.updateRaspberryEraseStation(data)),
    }
};

export default connect(
    null,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(RaspberryEraseStationList));